import { CheckCircle, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';

export function PixDialog({ open, onOpenChange, pixData }) {
  const { toast } = useToast();

  const copyPixCode = () => {
    if (pixData?.pix_code) {
      navigator.clipboard.writeText(pixData.pix_code);
      toast({ title: 'Código copiado!', description: 'Cole no app do seu banco.' });
    }
  };

  if (!pixData) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pix-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading font-bold uppercase text-primary text-center">
            Pagamento PIX
          </DialogTitle>
        </DialogHeader>
        <div className="text-center space-y-6">
          {pixData.qr_code_base64 && (
            <div className="bg-white p-4 rounded-lg inline-block">
              <img src={pixData.qr_code_base64} alt="QR Code PIX" className="w-48 h-48" />
            </div>
          )}

          <div>
            <p className="text-sm text-foreground/70 mb-2">Código PIX (Copia e Cola)</p>
            <div className="bg-secondary p-3 rounded-lg flex items-center gap-2">
              <code className="flex-1 text-xs break-all">{pixData.pix_code}</code>
              <Button size="sm" variant="outline" onClick={copyPixCode} className="rounded-sm">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="text-sm text-foreground/70">
            <p>Abra o app do seu banco</p>
            <p>Escolha pagar via PIX</p>
            <p>Escaneie o QR Code ou copie o código</p>
          </div>

          <p className="text-xs text-foreground/50">
            Após o pagamento, você receberá um email de confirmação.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function BoletoDialog({ open, onOpenChange, boletoData }) {
  const { toast } = useToast();

  const copyBoletoCode = () => {
    const code = boletoData?.boleto_formatted_barcode || boletoData?.boleto_barcode;
    if (code) {
      navigator.clipboard.writeText(code);
      toast({ title: 'Código copiado!', description: 'Cole no app do seu banco.' });
    }
  };

  if (!boletoData) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="boleto-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading font-bold uppercase text-primary text-center">
            Boleto Gerado
          </DialogTitle>
        </DialogHeader>
        <div className="text-center space-y-6">
          <div className="text-6xl">📄</div>

          <div>
            <p className="text-sm text-foreground/70 mb-2">Linha Digitável</p>
            <div className="bg-secondary p-3 rounded-lg flex items-center gap-2">
              <code className="flex-1 text-xs break-all">
                {boletoData.boleto_formatted_barcode || boletoData.boleto_barcode}
              </code>
              <Button size="sm" variant="outline" onClick={copyBoletoCode} className="rounded-sm">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {boletoData.boleto_due_date && (
            <p className="text-sm text-foreground/70">
              Vencimento: {new Date(boletoData.boleto_due_date).toLocaleDateString('pt-BR')}
            </p>
          )}

          {boletoData.boleto_pdf_url && (
            <Button
              asChild
              className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-bold px-6 py-3 rounded-sm"
            >
              <a href={boletoData.boleto_pdf_url} target="_blank" rel="noopener noreferrer">
                Baixar Boleto PDF
              </a>
            </Button>
          )}

          <p className="text-xs text-foreground/50">
            Após o pagamento (até 3 dias úteis), você receberá um email de confirmação.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ThankYouScreen({ registrationIds, onNewRegistration }) {
  return (
    <div data-testid="thank-you-screen" className="min-h-screen flex items-center justify-center px-6 py-24">
      <div className="max-w-md w-full text-center">
        <div className="bg-card border border-border rounded-lg p-8">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
          <h2 className="text-3xl font-heading font-bold uppercase text-primary mb-4">
            Inscrição Realizada!
          </h2>
          <p className="text-foreground/70 mb-6">
            {registrationIds.length > 1
              ? `${registrationIds.length} inscrições foram registradas com sucesso.`
              : 'Sua inscrição foi registrada com sucesso.'}
          </p>
          <p className="text-foreground/70 mb-6">
            Um email de confirmação foi enviado com os dados da inscrição e do pagamento.
          </p>

          <div className="bg-secondary p-4 rounded-lg mb-6">
            <p className="text-sm font-bold text-primary mb-2">Número(s) da Inscrição:</p>
            {registrationIds.map((id, index) => (
              <p key={id} className="text-sm text-foreground/70">{id}</p>
            ))}
          </div>

          <Button
            onClick={onNewRegistration}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-bold px-6 py-3 rounded-sm"
          >
            Nova Inscrição
          </Button>
        </div>
      </div>
    </div>
  );
}
